import { MigrationStatus, type MigrationOutcome, type Store } from '@wiztime/storage';
import type { i18n as I18nInstance } from 'i18next';
import { useState } from 'react';
import { exportAllData } from '../lib/exportFile';
import { Button, ButtonVariant } from '../ui/Button';
import styles from './MigrationBlocked.module.css';

/**
 * Start-up screen when the stored data cannot be opened for writing (data dictionary §10): a
 * newer build marked it incompatible, or an upgrade step failed. Only export and retry are offered.
 */
export function MigrationBlocked({
  store,
  outcome,
  i18n,
}: {
  store: Store;
  outcome: MigrationOutcome;
  i18n: I18nInstance;
}) {
  const t = i18n.t.bind(i18n);
  const [exported, setExported] = useState<string | null>(null);
  const tooNew = outcome.status === MigrationStatus.TooNew;
  return (
    <main className={styles.page} data-testid="migration-blocked" data-status={outcome.status}>
      <section className={styles.panel}>
        <h1 className={styles.title}>{t(tooNew ? 'migration.tooNewTitle' : 'migration.failedTitle')}</h1>
        <p>{t(tooNew ? 'migration.tooNewBody' : 'migration.failedBody')}</p>
        {outcome.error === undefined ? null : (
          <p className={styles.detail} data-testid="migration-error">
            {outcome.error}
          </p>
        )}
        <div className={styles.actions}>
          <Button
            variant={ButtonVariant.Primary}
            data-testid="migration-export"
            onClick={() => {
              void exportAllData(store, Date.now()).then((file) => setExported(file.filename));
            }}
          >
            {t('migration.export')}
          </Button>
          <Button data-testid="migration-retry" onClick={() => window.location.reload()}>
            {t('migration.retry')}
          </Button>
        </div>
        {exported === null ? null : <p className={styles.detail}>{t('migration.exported', { file: exported })}</p>}
      </section>
    </main>
  );
}
